import { useEffect, useRef, useState } from 'react';
import FilterChange from '../compo/filter_change';
import { fetchProperties, fetchConsultations } from '../utils/api';
import { useAuth, ROLES } from '../contexts/AuthContext';

const RANGE_FIELDS = [
  { key: '층수', label: '층수' },
  { key: '총층수', label: '총층수' },
  { key: '평수', label: '평수' },
  { key: '보증금', label: '보증금' },
  { key: '월세', label: '월세' },
  { key: '관리비', label: '관리비' },
  { key: '권리비', label: '권리비' },
];

const DEFAULT_SINGLE_OPTIONS = {
  엘베: ['유', '무'],
  화장실: ['내부', '외부']
};

const MULTI_FILTERS = [
  { key: '상권', label: '상권', type: 'multi' },
  { key: '용도', label: '용도', type: 'multi' }
];

const SINGLE_FILTERS = [
  { key: '엘베', label: '엘베', type: 'single' },
  { key: '화장실', label: '화장실', type: 'single' }
];

const RANGE_FILTERS = RANGE_FIELDS.map((field) => ({
  ...field,
  type: 'range'
}));

const FILTER_DEFINITIONS = [...MULTI_FILTERS, ...SINGLE_FILTERS, ...RANGE_FILTERS];

function Filter({ filters, onFilterChange, onReset }) {
  const { role } = useAuth();
  const isVisitor = role === ROLES.VISITOR;
  
  /** 0: fully collapsed, 1: main filters only, 2: all filters */
  const [expandState, setExpandState] = useState(0);
  const [options, setOptions] = useState({
    상권: [],
    용도: [],
    엘베: DEFAULT_SINGLE_OPTIONS.엘베,
    화장실: DEFAULT_SINGLE_OPTIONS.화장실
  });
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [primaryFilters, setPrimaryFilters] = useState(() =>
    ['상권', '층수', '평수', '월세', '보증금', '권리비']
  );
  const [isChangeOpen, setIsChangeOpen] = useState(false);
  const [손님목록, set손님목록] = useState([]); // 손님 목록
  const [isLoading손님목록, setIsLoading손님목록] = useState(false); // 손님 목록 로딩 중

  useEffect(() => {
    async function loadOptions() {
      setIsLoadingOptions(true);
      try {
        const data = await fetchProperties();
        const uniqueValues = (key) =>
          Array.from(new Set(data.map((item) => item?.[key]).filter(Boolean)));

        setOptions({
          상권: uniqueValues('상권'),
          용도: uniqueValues('용도'),
          엘베:
            uniqueValues('엘베').length > 0
              ? uniqueValues('엘베')
              : DEFAULT_SINGLE_OPTIONS.엘베,
          화장실:
            uniqueValues('화장실').length > 0
              ? uniqueValues('화장실')
              : DEFAULT_SINGLE_OPTIONS.화장실
        });
      } catch (error) {
        console.error('필터 옵션 로드 실패:', error);
      } finally {
        setIsLoadingOptions(false);
      }
    }

    loadOptions();
  }, []);

  // 손님 목록 로드 (항상 로드)
  useEffect(() => {
    async function load손님목록() {
      setIsLoading손님목록(true);
      try {
        const consultations = await fetchConsultations(role);
        // 고객 이름 목록 추출
        const customerNames = consultations
          .map(c => c.customerName)
          .filter(Boolean)
          .sort();
        set손님목록(customerNames);
      } catch (error) {
        console.error('손님 목록 로드 실패:', error);
        set손님목록([]);
      } finally {
        setIsLoading손님목록(false);
      }
    }
    
    load손님목록();
  }, [role]);

  const handleMultiSelectChange = (key, option, checked) => {
    if (isVisitor) return; // visitor는 필터 변경 불가
    
    const currentValues = Array.isArray(filters?.[key]) ? filters[key] : [];

    if (checked) {
      onFilterChange?.(key, [...currentValues, option]);
    } else {
      onFilterChange?.(
        key,
        currentValues.filter((item) => item !== option)
      );
    }
  };

  const handleSingleSelectChange = (key, value) => {
    if (isVisitor) return; // visitor는 필터 변경 불가
    onFilterChange?.(key, value === '전체' ? '' : value);
  };

  const renderMultiSelect = (key, label, optionsList, selectedValues) => {
    const selectedCount = selectedValues.length;
    const isOpen = openDropdown === key;
    const buttonLabel =
      selectedCount === 0
        ? '전체'
        : `${selectedCount}개 선택`;

  return (
    <div className="flex flex-col relative border border-gray-200 rounded-lg p-1 md:p-2 bg-gray-50" key={key}>
      <label className="text-[0.75rem] md:text-sm font-semibold text-gray-800 mb-0.5 md:mb-1.5 text-center whitespace-nowrap">
        {label}
      </label>
      <button
        type="button"
        onClick={() => !isVisitor && setOpenDropdown((prev) => (prev === key ? null : key))}
        disabled={isVisitor}
        className={`text-[0.875rem] md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1 text-left flex items-center justify-between focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none bg-white ${
          isVisitor ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
        }`}
      >
          <span className="truncate flex-1 min-w-0">
            {buttonLabel}
          </span>
          <svg
            className={`w-3 h-3 md:w-4 md:h-4 text-gray-500 transition-transform flex-shrink-0 ml-1 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-full sm:min-w-max max-w-[calc(100vw-2rem)] sm:max-w-none max-h-48 overflow-auto border border-gray-200 rounded-md bg-white shadow-lg z-30">
            <div className="flex items-center justify-between px-2 py-1 border-b border-gray-100 whitespace-nowrap">
              <span className="text-[0.875rem] text-gray-500">
                {label} 선택
              </span>
          <button
            type="button"
            onClick={() => !isVisitor && onFilterChange?.(key, [])}
            disabled={isVisitor}
            className={`text-[0.875rem] text-orange-500 hover:text-orange-600 ${
              isVisitor ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            전체
          </button>
            </div>
            <div className="max-h-40 overflow-auto">
              {optionsList.length === 0 ? (
                <p className="text-xs text-gray-400 px-2 py-3">
                  선택 가능한 값이 없습니다.
                </p>
              ) : (
                optionsList.map((option) => (
                  <label
                    key={option}
                    className="flex items-start gap-2 px-2 py-1 text-xs text-gray-700 hover:bg-orange-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={(e) =>
                        handleMultiSelectChange(key, option, e.target.checked)
                      }
                      disabled={isVisitor}
                      className="text-orange-500 rounded focus:ring-orange-500 flex-shrink-0 mt-0.5"
                    />
                    <span className="break-words min-w-0 flex-1">{option}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSingleSelect = (key, label, optionsList) => (
    <div className="flex flex-col border border-gray-200 rounded-lg p-1 md:p-2 bg-gray-50" key={key}>
      <label className="text-[0.75rem] md:text-sm font-semibold text-gray-800 mb-0.5 md:mb-1.5 text-center whitespace-nowrap">{label}</label>
      <select
        className={`text-[0.875rem] md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none bg-white ${
          isVisitor ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
        }`}
        value={filters?.[key] || '전체'}
        onChange={(e) => handleSingleSelectChange(key, e.target.value)}
        disabled={isVisitor}
      >
        <option value="전체">전체</option>
        {optionsList.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );

  const advancedContentRef = useRef(null);
  const [advancedHeight, setAdvancedHeight] = useState(0);

  useEffect(() => {
    if (advancedContentRef.current && expandState === 2) {
      setAdvancedHeight(advancedContentRef.current.scrollHeight);
    }
  }, [options, filters, expandState, primaryFilters]);

  const renderRangeField = (field) => {
    const rangeValue = filters?.[field.key] || { min: '', max: '' };
    const handleChange = (type, value) => {
      if (isVisitor) return; // visitor는 필터 변경 불가
      onFilterChange?.(field.key, {
        ...rangeValue,
        [type]: value
      });
    };

    return (
      <div key={field.key} className="flex flex-col border border-gray-200 rounded-lg p-1 md:p-2 bg-gray-50">
        <label className="text-[0.75rem] md:text-sm font-semibold text-gray-800 mb-0.5 md:mb-1.5 text-center whitespace-nowrap">
          {field.label}
        </label>
        <div className="flex items-center gap-0.5 md:gap-2">
          <input
            type="number"
            value={rangeValue.min || ''}
            onChange={(e) => handleChange('min', e.target.value)}
            placeholder="최소"
            disabled={isVisitor}
            className={`w-full text-[0.875rem] md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none bg-white ${
              isVisitor ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
            }`}
          />
          <span className="text-[0.75rem] md:text-xs text-gray-500 whitespace-nowrap">~</span>
          <input
            type="number"
            value={rangeValue.max || ''}
            onChange={(e) => handleChange('max', e.target.value)}
            placeholder="최대"
            disabled={isVisitor}
            className={`w-full text-[0.875rem] md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none bg-white ${
              isVisitor ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
            }`}
          />
        </div>
      </div>
    );
  };

  const getFilterDefinition = (key) =>
    FILTER_DEFINITIONS.find((item) => item.key === key);

  const renderPrimaryFilter = (key) => {
    const definition = getFilterDefinition(key);
    if (!definition) {
      return null;
    }

    if (definition.type === 'multi') {
      const selectedValues = Array.isArray(filters?.[key]) ? filters[key] : [];
      return renderMultiSelect(key, definition.label, options[key] || [], selectedValues);
    }

    if (definition.type === 'single') {
      const optionList = options[key] || DEFAULT_SINGLE_OPTIONS[key] || [];
      return renderSingleSelect(key, definition.label, optionList);
    }

    if (definition.type === 'range') {
      const field = RANGE_FIELDS.find((item) => item.key === key);
      if (!field) {
        return null;
      }
      return renderRangeField(field);
    }

    return null;
  };

  const handlePrimaryFilterApply = (selectedKeys) => {
    setPrimaryFilters(selectedKeys);
    setIsChangeOpen(false);
  };

  const advancedRangeFields = RANGE_FIELDS.filter(
    (field) => !primaryFilters.includes(field.key)
  );

  useEffect(() => {
    if (expandState !== 2) {
      setIsChangeOpen(false);
    }
  }, [expandState]);

  const handleToggleExpand = () => {
    if (isVisitor) return;
    setExpandState((prev) => (prev === 0 ? 1 : prev === 1 ? 2 : 0));
  };

  const toggleLabel = expandState === 0 ? '펼치기' : expandState === 1 ? '더 펼치기' : '접기';

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm relative overflow-visible pb-3 md:pb-4 z-10">
      {expandState >= 1 && (
        <div className="px-1.5 md:px-4 pb-1 md:pb-3">
          {isLoadingOptions ? (
            <p className="text-[0.75rem] md:text-xs text-gray-500 py-1">필터 옵션을 불러오는 중...</p>
          ) : (
            <div className={`grid grid-cols-2 gap-x-1 md:gap-x-3 gap-y-1 md:gap-y-2 mt-1 md:mt-2 ${
              primaryFilters.length === 1 ? 'md:grid-cols-1' :
              primaryFilters.length === 2 ? 'md:grid-cols-2' :
              primaryFilters.length === 3 ? 'md:grid-cols-3' :
              primaryFilters.length === 4 ? 'md:grid-cols-4' :
              primaryFilters.length === 5 ? 'md:grid-cols-5' :
              'md:grid-cols-6'
            }`}>
              {primaryFilters.map((key) => renderPrimaryFilter(key))}
            </div>
          )}
        </div>
      )}


      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          expandState === 2 ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ maxHeight: expandState === 2 ? advancedHeight || 9999 : 0 }}
      >
        <div ref={advancedContentRef} className="px-2 md:px-4 pt-1 md:pt-2 pb-4 md:pb-6">
          {/* 멀티 선택 필터 (상권, 용도) - primaryFilters에 없는 것만 표시 */}
          <div className="mb-2 md:mb-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-1 md:gap-x-3 gap-y-1 md:gap-y-2">
              {MULTI_FILTERS.filter(filter => !primaryFilters.includes(filter.key)).map((filter) => {
                const selectedValues = Array.isArray(filters?.[filter.key]) ? filters[filter.key] : [];
                return renderMultiSelect(filter.key, filter.label, options[filter.key] || [], selectedValues);
              })}
              {SINGLE_FILTERS.filter(filter => !primaryFilters.includes(filter.key)).map((filter) => {
                const optionList = options[filter.key] || DEFAULT_SINGLE_OPTIONS[filter.key] || [];
                return renderSingleSelect(filter.key, filter.label, optionList);
              })}
              {/* 이름 검색 필터 */}
              <div className="flex flex-col border border-gray-200 rounded-lg p-1 md:p-2 bg-gray-50">
                <label className="text-[0.75rem] md:text-sm font-semibold text-gray-800 mb-0.5 md:mb-1.5 text-center whitespace-nowrap">
                  이름 검색
                </label>
                <input
                  type="text"
                  value={filters?.이름검색 || ''}
                  onChange={(e) => {
                    if (isVisitor) return;
                    onFilterChange?.('이름검색', e.target.value);
                  }}
                  placeholder="상호명 입력"
                  disabled={isVisitor}
                  className={`text-[0.875rem] md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none bg-white ${
                    isVisitor ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                  }`}
                />
              </div>
              {/* 관심 매물 필터 - 다른 필터와 동일한 크기 */}
              <div className="flex flex-col border border-gray-200 rounded-lg p-1 md:p-2 bg-gray-50">
                <label className="text-[0.75rem] md:text-sm font-semibold text-gray-800 mb-0.5 md:mb-1.5 text-center whitespace-nowrap">
                  관심 매물
                </label>
                {isLoading손님목록 ? (
                  <div className="flex items-center justify-center gap-1.5 py-1">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-orange-500"></div>
                    <span className="text-[0.75rem] text-gray-500">로딩 중...</span>
                  </div>
                ) : (
                  <select
                    value={filters?.관심매물손님 || '전체'}
                    onChange={(e) => {
                      if (isVisitor) return;
                      const value = e.target.value === '전체' ? undefined : e.target.value;
                      onFilterChange?.('관심매물손님', value);
                      onFilterChange?.('관심매물', value ? true : undefined);
                    }}
                    disabled={isVisitor}
                    className={`text-[0.875rem] md:text-sm border border-gray-300 rounded px-1 md:px-2 py-0.5 md:py-1 focus:border-orange-400 focus:ring-1 focus:ring-orange-400 outline-none bg-white ${
                      isVisitor ? 'opacity-50 cursor-not-allowed bg-gray-100' : ''
                    }`}
                  >
                    <option value="전체">전체</option>
                    {손님목록.map((손님명) => (
                      <option key={손님명} value={손님명}>
                        {손님명}
                      </option>
                    ))}
                  </select>
                )}
                {손님목록.length === 0 && !isLoading손님목록 && (
                  <p className="text-[0.75rem] text-gray-500 text-center mt-1">손님 없음</p>
                )}
              </div>
            </div>
          </div>

          {/* 추가 범위 필터 */}
          {advancedRangeFields.length === 0 ? (
            <p className="text-[0.75rem] md:text-xs text-gray-500">추가로 설정할 범위 필터가 없습니다.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-1 md:gap-x-3 gap-y-1 md:gap-y-2">
              {advancedRangeFields.map((field) => renderRangeField(field))}
            </div>
          )}
        </div>
      </div>

      {expandState === 2 && (
        <div className="absolute right-2 md:right-4 bottom-5 flex items-center gap-1.5 md:gap-2 z-20">
          <button
            type="button"
            onClick={() => !isVisitor && onReset?.()}
            disabled={isVisitor}
            className={`px-3 md:px-4 py-1.5 border border-orange-400 text-orange-500 text-xs md:text-sm font-semibold rounded-full shadow-sm hover:bg-orange-50 transition-colors ${
              isVisitor ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            초기화
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => !isVisitor && setIsChangeOpen((prev) => !prev)}
              disabled={isVisitor}
              className={`px-3 md:px-4 py-1.5 border border-gray-300 text-xs md:text-sm font-semibold rounded-full bg-gray-50 hover:bg-gray-100 ${
                isVisitor ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              변경
            </button>
            {isChangeOpen && (
              <FilterChange
                filters={FILTER_DEFINITIONS}
                selectedKeys={primaryFilters}
                maxSelection={6}
                onApply={handlePrimaryFilterApply}
                onClose={() => setIsChangeOpen(false)}
              />
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleToggleExpand}
        disabled={isVisitor}
        className={`absolute left-1/2 transform -translate-x-1/2 bottom-[-12px] md:bottom-[-10px] z-30 h-7 md:h-9 w-auto min-w-[80px] md:min-w-[100px] px-2 md:px-4 rounded-full border border-gray-300 bg-white flex items-center justify-center gap-1 md:gap-1.5 shadow-md hover:shadow-lg transition-shadow ${
          isVisitor ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <span className="text-[0.7rem] md:text-sm font-medium text-gray-700 whitespace-nowrap">{toggleLabel}</span>
        <svg
          className={`w-2.5 h-2.5 md:w-3 md:h-3 text-orange-500 transition-transform flex-shrink-0 ${expandState >= 1 ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    </div>
  );
}

export default Filter;